// The lattice of rites: the same 3×3×3 cube as the deck, one rite at each
// point, shown as three tablets (sink, still, rise). Which rites are open:
//
// - the first rite is always open, and its win opens only the second;
// - a rite with a game in progress stays open;
// - the sink tablet waits until the still one is whole (all nine won: it
//   folds into one card), the rise tablet until the sink one is whole
//   (numbers in src/config/rules.ts);
// - otherwise a rite opens when it has one won neighbour (one notch away in
//   one ring) for each of its rings that is not level, so the edges of a
//   tablet open before its corners.
//
// Three won rites in a straight line light the line. When all three tablets
// are whole, the Ternion opens: the three folded tablets, to fold as one.

import { RULES } from "../config/rules.ts"
import { ALL_RITE_IDS, pointOfRite, riteIdOf, TERNION_ID, type RiteId } from "./rite.ts"
import type { Trit } from "./rings.ts"
import type { Progress } from "./progress.ts"
import { inPlay } from "./progress.ts"

export type NodeState = "locked" | "open" | "solved" | "pure"
export type Layer = { readonly z: Trit; readonly name: "sink" | "still" | "rise" }

export const LAYERS: readonly Layer[] = [
  { z: -1, name: "sink" },
  { z: 0, name: "still" },
  { z: 1, name: "rise" },
]

const TRITS: readonly Trit[] = [-1, 0, 1]
const AXES = [0, 1, 2] as const
type Axis = (typeof AXES)[number]

const step = (t: number): Trit | undefined => (t === -1 || t === 0 || t === 1 ? t : undefined)

const shifted = (p: readonly [Trit, Trit, Trit], axis: Axis, d: number): RiteId | undefined => {
  const moved = step(p[axis] + d)
  if (moved === undefined) return undefined
  const q: readonly [Trit, Trit, Trit] = [axis === 0 ? moved : p[0], axis === 1 ? moved : p[1], axis === 2 ? moved : p[2]]
  return riteIdOf(...q)
}

const neighboursAlong =
  (p: readonly [Trit, Trit, Trit]) =>
  (axis: Axis): readonly RiteId[] =>
    [shifted(p, axis, -1), shifted(p, axis, 1)].filter((x) => x !== undefined)

// The rites one notch away in one ring.
export const neighbours = (id: RiteId): readonly RiteId[] => AXES.flatMap(neighboursAlong(pointOfRite(id)))

export type Line = { readonly axis: Axis; readonly pts: readonly [RiteId, RiteId, RiteId] }

const lineThrough = (axis: Axis, a: Trit, b: Trit): Line => {
  const at = (v: Trit): RiteId =>
    axis === 0 ? riteIdOf(v, a, b) : axis === 1 ? riteIdOf(b, v, a) : riteIdOf(a, b, v)
  return { axis, pts: [at(-1), at(0), at(1)] }
}
const linesAlongWith = (axis: Axis, a: Trit): readonly Line[] => TRITS.map((b) => lineThrough(axis, a, b))
const linesAlong = (axis: Axis): readonly Line[] => TRITS.flatMap((a) => linesAlongWith(axis, a))

// Every straight line of three points in the cube: 27 of them.
export const LINES: readonly Line[] = AXES.flatMap(linesAlong)

export const isSolved = (progress: Progress, id: RiteId): boolean => progress.solved[id] !== undefined

export const solvedIn = (progress: Progress, z: Trit): number =>
  ALL_RITE_IDS.filter((id) => pointOfRite(id)[2] === z && isSolved(progress, id)).length

export const solvedCount = (progress: Progress): number => ALL_RITE_IDS.filter((id) => isSolved(progress, id)).length

const onlyFirstWon = (progress: Progress): boolean => solvedCount(progress) === 1 && isSolved(progress, RULES.firstRite)

const layerShut = (progress: Progress, z: Trit): boolean =>
  (z === -1 && solvedIn(progress, 0) < RULES.sinkNeedsStillWins) || (z === 1 && solvedIn(progress, -1) < RULES.riseNeedsSinkWins)

// `exists` says whether a rite is in the loaded campaign.
export const nodeState = (progress: Progress, exists: (id: RiteId) => boolean, id: RiteId): NodeState => {
  const won = progress.solved[id]
  if (won !== undefined) return won.pure ? "pure" : "solved"
  if (!exists(id)) return "locked"
  if (id === RULES.firstRite || inPlay(progress, id)) return "open"
  if (onlyFirstWon(progress)) return id === RULES.secondRite ? "open" : "locked"
  const p = pointOfRite(id)
  if (layerShut(progress, p[2])) return "locked"
  const need = Math.max(1, p.filter((x) => x !== 0).length)
  return neighbours(id).filter((n) => isSolved(progress, n)).length >= need ? "open" : "locked"
}

export const litLines = (progress: Progress): readonly Line[] => LINES.filter((l) => l.pts.every((p) => isSolved(progress, p)))

const TABLET_SIZE = 9

// A tablet is whole when all nine of its rites are won: it folds into one card.
export const tabletWhole = (progress: Progress, z: Trit): boolean => solvedIn(progress, z) === TABLET_SIZE

// The Ternion opens when every tablet is whole.
export const ternionState = (progress: Progress): NodeState => {
  const won = progress.solved[TERNION_ID]
  if (won !== undefined) return won.pure ? "pure" : "solved"
  return solvedCount(progress) === ALL_RITE_IDS.length || inPlay(progress, TERNION_ID) ? "open" : "locked"
}
